import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsService, type AnalyticsDashboard, type AnalyticsRange, type Metric, type StatsSummary } from '../../services/analytics.service';
import { analyticsOverviewService, type AnalyticsOverview } from '../../services/analyticsOverview';
import { PLATFORM_LABEL, socialService, type PostMetrics, type ScheduledPost, type SocialAccount, type SocialPlatform } from '../../services/social.service';
import { FONT_SANS } from '../landing/landingData';
import { EmptyBox, FillButton, OutlineButton, PageHead, fmtAgo, useDash, useDismiss, type DashProject } from './dashUi';

/**
 * Dashboard Analytics (design "HyVay Dashboard" → Analytics, 2026-11).
 * Website tab: Umami numbers of every live site summed (/analytics/overview) or of one
 * site (/analytics/:id, which also carries sources, locations, devices and busiest times).
 * Video tab: platform insights of published social posts (views, likes, …) aggregated here.
 */

// ---------------------------------------------------------------- constants & helpers
const TZ = (() => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Jakarta';
    } catch {
        return 'Asia/Jakarta';
    }
})();

type VideoRange = AnalyticsRange | 'all';
const RANGES: Array<[AnalyticsRange, string]> = [
    ['24h', 'Last 24 hours'],
    ['7d', 'Last 7 days'],
    ['30d', 'Last 30 days'],
    ['90d', 'Last 90 days'],
    ['12m', 'Last 12 months'],
];
const RANGE_MS: Record<AnalyticsRange, number> = { '24h': 864e5, '7d': 7 * 864e5, '30d': 30 * 864e5, '90d': 90 * 864e5, '12m': 365 * 864e5 };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Design donut / bar palette. */
const PIE = ['#13422C', '#4C9A6E', '#A8D5B9', '#E0A43B', '#C9774E', '#B8B2A4'];
const GOOD = 'var(--hv-leaf,#2E8B57)';
const BAD = 'var(--hv-danger)';

const fmtD = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
function rangeDates(range: AnalyticsRange): string {
    const end = new Date();
    const start = new Date(end.getTime() - RANGE_MS[range]);
    if (range === '24h') return start.getDate() === end.getDate() ? fmtD(end) : `${fmtD(start)} – ${fmtD(end)}`;
    return `${fmtD(start)} – ${fmtD(end)}${start.getFullYear() !== end.getFullYear() ? ` ${end.getFullYear()}` : ''}`;
}

/** Design "k" numbers: 2.5k, 12k, 1.2M. */
function fmtK(n: number): string {
    const v = Math.round(n || 0);
    if (v >= 1e6) return `${(v / 1e6).toFixed(v >= 1e7 ? 0 : 1).replace('.0', '')}M`;
    if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1).replace('.0', '')}k`;
    return String(v);
}
const fmtInt = (n: number) => new Intl.NumberFormat('en-US').format(Math.round(n || 0));
function fmtDuration(sec: number): string {
    const s = Math.round(sec);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    return m < 60 ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}
const titleCase = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
let regionNames: Intl.DisplayNames | null = null;
function countryName(code: string): string {
    try {
        regionNames ??= new Intl.DisplayNames(['en'], { type: 'region' });
        return regionNames.of(code.toUpperCase()) || code;
    } catch {
        return code;
    }
}
function hostOf(url?: string | null): string {
    if (!url) return '';
    try {
        return new URL(url).host;
    } catch {
        return url.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    }
}

/** Umami labels buckets with wall-clock time in the requested zone; compare on the unit's prefix. */
const keyOf = (x: string, unit: string) => x.slice(0, unit === 'hour' ? 13 : unit === 'day' ? 10 : 7);
/** Every bucket in [startAt, endAt] so empty hours/days still get a point. */
function bucketKeys(startAt: number, endAt: number, unit: string, tz: string): string[] {
    let fmt: Intl.DateTimeFormat;
    try {
        fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' });
    } catch {
        fmt = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' });
    }
    const step = unit === 'hour' ? 3600_000 : 86400_000 / 4;
    const keys: string[] = [];
    for (let t = startAt; t <= endAt; t += step) {
        const p = Object.fromEntries(fmt.formatToParts(new Date(t)).map((x) => [x.type, x.value]));
        const k = unit === 'hour' ? `${p.year}-${p.month}-${p.day}T${p.hour}` : unit === 'day' ? `${p.year}-${p.month}-${p.day}` : `${p.year}-${p.month}`;
        if (keys[keys.length - 1] !== k) keys.push(k);
    }
    return keys;
}
function bucketLabels(key: string, unit: string): { short: string; when: string } {
    const [y, m, rest = ''] = key.split('-');
    const mon = MONTHS[Number(m) - 1];
    if (unit === 'month') return { short: Number(m) === 1 ? `${mon} ${y}` : mon, when: `${mon} ${y}` };
    const d = Number(rest.slice(0, 2));
    if (unit === 'day') {
        const dow = DOW[new Date(Number(y), Number(m) - 1, d).getDay()];
        return { short: `${d} ${mon}`, when: `${dow}, ${d} ${mon} ${y}` };
    }
    const hh = `${rest.slice(3, 5)}:00`;
    return { short: hh, when: `${d} ${mon}, ${hh}` };
}

// ---------------------------------------------------------------- website view model
interface PageRow {
    key: string;
    path: string;
    sub: string;
    value: number;
}
interface WebData {
    unit: 'hour' | 'day' | 'month';
    timezone: string;
    startAt: number;
    endAt: number;
    summary: StatsSummary;
    previous: StatsSummary;
    series: { pageviews: Metric[]; sessions: Metric[] };
    leads: { whatsapp: number; phone: number; maps: number; email: number };
    pages: PageRow[];
    /** Single site only. */
    single: null | {
        entries: PageRow[];
        referrers: Metric[];
        utmCampaigns: Metric[];
        utmSources: Metric[];
        countries: Metric[];
        cities: Metric[];
        devices: Metric[];
        browsers: Metric[];
        weekly: number[][] | null;
    };
    active: number;
}
type WebState =
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'disabled' }
    | { kind: 'nolive' }
    | { kind: 'ready'; data: WebData };

const ZERO: StatsSummary = { pageviews: 0, visitors: 0, visits: 0, bounces: 0, totaltime: 0 };
const NO_LEADS = { whatsapp: 0, phone: 0, maps: 0, email: 0 };

function fromOverview(o: AnalyticsOverview, range: AnalyticsRange): WebData {
    const endAt = o.endAt ?? Date.now();
    return {
        unit: o.unit,
        timezone: o.timezone || TZ,
        startAt: o.startAt ?? endAt - RANGE_MS[range],
        endAt,
        summary: o.summary ?? ZERO,
        previous: o.previous ?? ZERO,
        series: o.series ?? { pageviews: [], sessions: [] },
        leads: o.leads ?? NO_LEADS,
        pages: (o.pages ?? []).map((p, i) => ({ key: `${p.site}${p.x}${i}`, path: p.x, sub: p.site, value: p.y })),
        single: null,
        active: o.active ?? 0,
    };
}
function fromSite(d: AnalyticsDashboard, range: AnalyticsRange, host: string): WebData {
    const endAt = d.endAt ?? Date.now();
    const rows = (list?: Metric[]) => (list ?? []).map((p, i) => ({ key: `${p.x}${i}`, path: p.x, sub: host, value: p.y }));
    return {
        unit: d.unit ?? 'day',
        timezone: d.timezone || TZ,
        startAt: d.startAt ?? endAt - RANGE_MS[range],
        endAt,
        summary: d.summary ?? ZERO,
        previous: d.previous ?? ZERO,
        series: d.series ?? { pageviews: [], sessions: [] },
        leads: d.leads ?? NO_LEADS,
        pages: rows(d.pages),
        single: {
            entries: rows(d.entries),
            referrers: d.referrers ?? [],
            utmCampaigns: d.utmCampaigns ?? [],
            utmSources: d.utmSources ?? [],
            countries: (d.countries ?? []).map((r) => ({ ...r, x: countryName(r.x) })),
            cities: d.cities ?? [],
            devices: (d.devices ?? []).map((r) => ({ ...r, x: titleCase(r.x) })),
            browsers: (d.browsers ?? []).map((r) => ({ ...r, x: titleCase(r.x) })),
            weekly: d.weekly ?? null,
        },
        active: d.active ?? 0,
    };
}

// ---------------------------------------------------------------- small primitives
const CARD: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0, padding: '16px 20px 20px', borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)' };

const PAGE_CSS = `
.hv-dash .hv-an-tab:hover { color: var(--hv-ink) !important; }
.hv-dash .hv-an-dd:hover { border-color: var(--hv-brand-ink) !important; }
.hv-dash .hv-an-seg:hover { color: var(--hv-ink) !important; }
.hv-dash .hv-an-row:hover { background: var(--hv-card-2); }
.hv-dash .hv-an-pg:hover:not(:disabled) { background: var(--hv-page-2) !important; }
.hv-dash .hv-an-sel:focus { border-color: var(--hv-brand-ink) !important; }
.hv-dash .hv-an-q:focus-within { border-color: var(--hv-brand-ink) !important; }
`;

const Skel: React.FC<{ h: number; w?: number | string; r?: number; style?: React.CSSProperties }> = ({ h, w = '100%', r = 8, style }) => (
    <div aria-hidden="true" style={{ width: w, height: h, borderRadius: r, background: 'var(--hv-page-2)', animation: 'hvSkel 1.6s ease-in-out infinite', ...style }} />
);

const ChevronSm: React.FC<{ open?: boolean }> = ({ open }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', color: 'var(--hv-muted)', transform: `rotate(${open ? 180 : 0}deg)`, transition: 'transform 0.2s ease' }} aria-hidden="true">
        <path d="M6 9l6 6 6-6" />
    </svg>
);
const CheckSm: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--hv-brand-ink)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }} aria-hidden="true">
        <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
);
const CalIcon: React.FC = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', color: 'var(--hv-body)' }} aria-hidden="true">
        <path d="M6.5 5.5h11A2.5 2.5 0 0 1 20 8v9.5a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5V8a2.5 2.5 0 0 1 2.5-2.5z M4 10h16 M8.5 3.5v3.5 M15.5 3.5v3.5" />
    </svg>
);
const RefreshIcon: React.FC<{ spin?: boolean }> = ({ spin }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" style={{ animation: spin ? 'hvSpin 0.8s linear infinite' : undefined }} aria-hidden="true">
        <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3 M19.5 4.5v4h-4" />
    </svg>
);

interface Opt {
    id: string;
    label: string;
    sub?: string;
}
/** Design toolbar dropdown (280px box, popover list; optional search). */
const SelectBox: React.FC<{ value: string; options: Opt[]; onChange: (id: string) => void; ariaLabel: string; display: React.ReactNode; searchPh?: string }> = ({ value, options, onChange, ariaLabel, display, searchPh }) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const close = useCallback(() => setOpen(false), []);
    const ref = useDismiss<HTMLDivElement>(open, close);
    const query = q.trim().toLowerCase();
    const shown = query ? options.filter((o) => `${o.label} ${o.sub ?? ''}`.toLowerCase().includes(query)) : options;
    return (
        <div style={{ position: 'relative', maxWidth: '100%' }}>
            <button type="button" data-dismiss-ignore onClick={() => { setOpen((o) => !o); setQ(''); }} aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} className="hv-an-dd" style={{ display: 'flex', alignItems: 'center', gap: 8, width: 280, maxWidth: '100%', height: 36, padding: '0 12px', borderRadius: 6, border: `1px solid ${open ? 'var(--hv-brand-ink)' : 'var(--hv-line-2)'}`, background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {display}
                <ChevronSm open={open} />
            </button>
            {open && (
                <div ref={ref} role="listbox" aria-label={ariaLabel} style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 30, width: 280, maxWidth: 'calc(100vw - 32px)', maxHeight: 340, overflowY: 'auto', padding: 6, borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 20px 40px -18px var(--hv-shadow)', animation: 'hvPop 0.18s ease both' }}>
                    {searchPh && (
                        <div style={{ position: 'sticky', top: -6, zIndex: 2, margin: '-6px -6px 4px', padding: '10px 14px 6px', background: 'var(--hv-card)' }}>
                            <label className="hv-an-q" style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: '0 10px', borderRadius: 6, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)', color: 'var(--hv-muted)' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flex: 'none' }} aria-hidden="true"><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" /></svg>
                                <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPh} aria-label={searchPh} autoFocus style={{ flex: 1, minWidth: 0, height: '100%', border: 0, outline: 'none', background: 'transparent', color: 'var(--hv-ink)', font: 'inherit', fontSize: 13 }} />
                            </label>
                        </div>
                    )}
                    {!shown.length && <div style={{ padding: '10px 8px 16px', fontSize: 12.5, color: 'var(--hv-muted)', textAlign: 'center' }}>No matches for “{q}”.</div>}
                    {shown.map((o) => {
                        const on = o.id === value;
                        return (
                            <button key={o.id} type="button" role="option" aria-selected={on} onClick={() => { onChange(o.id); setOpen(false); }} className="hv-mi" style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 36, padding: '6px 10px', border: 0, borderRadius: 5, background: on ? 'color-mix(in srgb,var(--hv-mint) 55%,var(--hv-card))' : 'transparent', color: 'var(--hv-ink)', fontSize: 13, textAlign: 'left', cursor: 'pointer' }}>
                                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</span>
                                    {o.sub && <span style={{ fontSize: 11.5, color: 'var(--hv-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.sub}</span>}
                                </span>
                                {on && <CheckSm />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const RangeSelect: React.FC<{ value: string; options: Array<[string, string]>; onChange: (v: string) => void }> = ({ value, options, onChange }) => {
    const label = options.find((o) => o[0] === value)?.[1] ?? options[0][1];
    const dates = value === 'all' ? '' : rangeDates(value as AnalyticsRange);
    return (
        <SelectBox
            value={value}
            ariaLabel="Date range"
            options={options.map(([id, l]) => ({ id, label: l }))}
            onChange={onChange}
            display={
                <>
                    <CalIcon />
                    <span style={{ fontWeight: 500 }}>{label}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left', color: 'var(--hv-muted)' }}>{dates}</span>
                </>
            }
        />
    );
};

/** Design pill segmented control (Visitors / Page views, Most viewed / Landing pages…). */
function Seg<V extends string>({ value, options, onChange }: { value: V; options: Array<[V, string]>; onChange: (v: V) => void }) {
    return (
        <div role="tablist" style={{ flex: 'none', display: 'inline-flex', alignSelf: 'flex-start', gap: 2, padding: 2, borderRadius: 999, background: 'var(--hv-page-2)' }}>
            {options.map(([v, label]) => {
                const on = v === value;
                return (
                    <button key={v} type="button" role="tab" aria-selected={on} onClick={() => onChange(v)} className="hv-an-seg" style={{ height: 28, padding: '0 12px', border: 0, borderRadius: 999, background: on ? 'var(--hv-card)' : 'transparent', boxShadow: on ? '0 1px 2px rgba(19,66,44,0.12)' : 'none', color: on ? 'var(--hv-ink)' : 'var(--hv-body)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        {label}
                    </button>
                );
            })}
        </div>
    );
}

const CardHead: React.FC<{ title: string; sub?: React.ReactNode; right?: React.ReactNode }> = ({ title, sub, right }) => (
    <div style={{ display: 'flex', alignItems: sub ? 'flex-start' : 'center', flexWrap: 'wrap', gap: '8px 12px', minHeight: 32 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{title}</span>
            {sub && <span style={{ fontSize: 12.5, color: 'var(--hv-muted)' }}>{sub}</span>}
        </div>
        {right}
    </div>
);

interface Kpi {
    label: string;
    value: string;
    /** Relative change in %, null when there is nothing to compare with. */
    pct?: number | null;
    upIsGood?: boolean;
    /** Replaces the "vs previous period" line (video KPIs). */
    note?: string;
}
const KpiRow: React.FC<{ items: Kpi[] }> = ({ items }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
        {items.map((k) => {
            const has = k.pct !== undefined && k.pct !== null && Number.isFinite(k.pct);
            const pct = has ? Math.round(k.pct as number) : 0;
            const good = pct === 0 || (pct > 0) === (k.upIsGood !== false);
            return (
                <div key={k.label} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '16px 18px', borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--hv-body)' }}>{k.label}</span>
                    <span style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--hv-ink)', fontVariantNumeric: 'tabular-nums' }}>{k.value}</span>
                    <span style={{ fontSize: 12, color: 'var(--hv-muted)' }}>
                        {k.note !== undefined ? k.note : has ? (
                            <>
                                <span style={{ color: good ? GOOD : BAD, fontWeight: 500 }}>{`${pct >= 0 ? '+' : '−'}${Math.abs(pct)}%`}</span> vs previous period
                            </>
                        ) : (
                            'No data for the previous period'
                        )}
                    </span>
                </div>
            );
        })}
    </div>
);

/** Design pager: "Showing 1–5 of 12" + prev / page select / next. */
function usePaged<T>(list: T[], per: number, resetKey: string) {
    const [page, setPage] = useState(0);
    useEffect(() => setPage(0), [resetKey]);
    const pages = Math.max(1, Math.ceil(list.length / per));
    const p = Math.min(page, pages - 1);
    return { items: list.slice(p * per, (p + 1) * per), page: p, pages, total: list.length, per, setPage };
}
const Pager: React.FC<{ page: number; pages: number; total: number; per: number; setPage: (n: number) => void }> = ({ page, pages, total, per, setPage }) => {
    const from = total ? page * per + 1 : 0;
    const to = Math.min(total, (page + 1) * per);
    const btn = (dis: boolean): React.CSSProperties => ({ width: 32, height: 32, borderRadius: 8, border: 0, background: 'transparent', color: 'var(--hv-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: dis ? 'default' : 'pointer', opacity: dis ? 0.4 : 1 });
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '14px 4px 0' }}>
            <span style={{ fontSize: 12.5, color: 'var(--hv-muted)' }}>{total ? `Showing ${from}–${to} of ${total}` : 'No results'}</span>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button type="button" aria-label="Previous page" disabled={page === 0} onClick={() => setPage(page - 1)} className="hv-an-pg" style={btn(page === 0)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
                </button>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 6px', fontSize: 12.5, color: 'var(--hv-muted)' }}>
                    Page
                    <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <select value={page} onChange={(e) => setPage(parseInt(e.target.value, 10) || 0)} aria-label="Go to page" className="hv-an-sel" style={{ appearance: 'none', WebkitAppearance: 'none', height: 32, padding: '0 28px 0 12px', borderRadius: 6, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', color: 'var(--hv-ink)', fontSize: 12.5, cursor: 'pointer', outline: 'none' }}>
                            {Array.from({ length: pages }, (_, i) => <option key={i} value={i}>{i + 1}</option>)}
                        </select>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', right: 9, pointerEvents: 'none', color: 'var(--hv-muted)' }} aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                    </span>
                    of {pages}
                </label>
                <button type="button" aria-label="Next page" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="hv-an-pg" style={btn(page >= pages - 1)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                </button>
            </div>
        </div>
    );
};

/** Design table: grey header strip + 48px rows. `cols` is the grid template. */
const Table: React.FC<{ cols: string; head: Array<[string, 'left' | 'right']>; rows: Array<{ key: string; cells: React.ReactNode[] }>; empty?: string }> = ({ cols, head, rows, empty }) => (
    <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: head.length > 4 ? 640 : 0 }}>
            <div style={{ display: 'grid', gap: 24, alignItems: 'center', padding: '0 16px', height: 40, background: 'var(--hv-page-2)', borderTop: '1px solid var(--hv-line)', borderBottom: '1px solid var(--hv-line)', fontSize: 12, fontWeight: 600, color: 'var(--hv-body)', gridTemplateColumns: cols }}>
                {head.map(([h, a]) => <span key={h} style={{ textAlign: a }}>{h}</span>)}
            </div>
            {rows.length ? rows.map((r) => (
                <div key={r.key} className="hv-an-row" style={{ display: 'grid', gap: 24, alignItems: 'center', padding: '0 16px', minHeight: 48, borderBottom: '1px solid var(--hv-line)', fontSize: 13, color: 'var(--hv-ink)', gridTemplateColumns: cols, fontVariantNumeric: 'tabular-nums' }}>
                    {r.cells.map((c, i) => <React.Fragment key={i}>{c}</React.Fragment>)}
                </div>
            )) : (
                <div style={{ padding: '18px 16px', borderBottom: '1px solid var(--hv-line)', fontSize: 13, color: 'var(--hv-muted)' }}>{empty ?? 'Nothing here yet.'}</div>
            )}
        </div>
    </div>
);
const ellipsis: React.CSSProperties = { minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

// ---------------------------------------------------------------- chart
interface ChartPoint {
    key: string;
    short: string;
    when: string;
    value: number;
    rows: Array<[string, string]>;
}
const LineChart: React.FC<{ points: ChartPoint[]; unit: string }> = ({ points, unit }) => {
    const [hv, setHv] = useState<number | null>(null);
    const plotRef = useRef<HTMLDivElement>(null);
    const N = points.length;
    const vals = points.map((p) => p.value);
    const rawMax = Math.max(1, ...vals) * 1.08;
    const mag = Math.pow(10, Math.floor(Math.log10(rawMax / 4)));
    const step = Math.max(1, [1, 2, 2.5, 5, 10].map((x) => x * mag).find((x) => x * 4 >= rawMax) ?? 10 * mag);
    const max = step * 4;
    const X = (i: number) => (N <= 1 ? 300 : (i / (N - 1)) * 600);
    const Y = (v: number) => 200 - (v / max) * 200;
    const line = points.map((p, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(p.value).toFixed(1)}`).join(' ');
    const area = N ? `${line} L${X(N - 1).toFixed(1)} 200 L${X(0).toFixed(1)} 200 Z` : '';
    const grid = [1, 0.75, 0.5, 0.25, 0].map((f) => ({ f, label: fmtK(max * f), top: `${(1 - f) * 100}%` }));
    const xIdx = N <= 8 ? points.map((_, i) => i) : [...new Set([0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * (N - 1))))];
    const showAllDots = N <= 31;
    const chWord = unit === 'hour' ? 'vs previous hour' : unit === 'day' ? 'vs previous day' : 'vs previous month';

    const onMove = (e: React.MouseEvent) => {
        const el = plotRef.current;
        if (!el || !N) return;
        const r = el.getBoundingClientRect();
        const f = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
        setHv(N <= 1 ? 0 : Math.round(f * (N - 1)));
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '40px minmax(0,1fr)', gridTemplateRows: '200px 22px', columnGap: 10, marginTop: 6 }}>
            <div style={{ position: 'relative' }}>
                {grid.map((g) => <span key={g.f} style={{ position: 'absolute', right: 0, top: g.top, transform: 'translateY(-50%)', fontSize: 11, color: 'var(--hv-muted)' }}>{g.label}</span>)}
            </div>
            <div ref={plotRef} onMouseMove={onMove} onMouseLeave={() => setHv(null)} style={{ position: 'relative', cursor: N ? 'crosshair' : 'default' }}>
                {grid.map((g) => <span key={g.f} style={{ position: 'absolute', left: 0, right: 0, top: g.top, height: 1, background: 'var(--hv-line)' }} />)}
                <svg viewBox="0 0 600 200" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }} aria-hidden="true">
                    {N > 0 && <path d={area} fill="var(--hv-mint)" opacity="0.55" />}
                    {N > 0 && <path d={line} fill="none" stroke="var(--hv-brand-ink)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />}
                </svg>
                {hv !== null && hv < N && <span style={{ position: 'absolute', top: 0, bottom: 0, left: `${(X(hv) / 6).toFixed(2)}%`, width: 0, borderLeft: '1px dashed var(--hv-line-2)', pointerEvents: 'none' }} />}
                {points.map((p, i) => {
                    const on = hv === i;
                    if (!showAllDots && !on) return null;
                    const L = X(i) / 6;
                    const T = Y(p.value) / 2;
                    const prev = i ? points[i - 1].value : null;
                    const ch = prev ? Math.round(((p.value - prev) / prev) * 100) : null;
                    const dot = on ? 10 : 7;
                    return (
                        <span key={p.key} style={{ position: 'absolute', left: `${L.toFixed(2)}%`, top: `${T.toFixed(2)}%`, width: 20, height: 20, margin: '-10px 0 0 -10px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: on ? 3 : 2, pointerEvents: 'none' }}>
                            <span style={{ width: dot, height: dot, borderRadius: 999, background: 'var(--hv-brand-ink)', border: '1.5px solid var(--hv-brand-ink)', boxShadow: '0 0 0 3px var(--hv-card)', transition: 'width 0.12s ease,height 0.12s ease' }} />
                            {on && (
                                <span role="tooltip" style={{ position: 'absolute', top: T < 45 ? 'calc(100% + 6px)' : 'auto', bottom: T < 45 ? 'auto' : 'calc(100% + 6px)', left: L < 15 ? -6 : L > 85 ? 22 : '50%', transform: `translateX(${L < 15 ? '0' : L > 85 ? '-100%' : '-50%'})`, zIndex: 20, width: 200, padding: '10px 12px', borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 16px 32px -14px var(--hv-shadow)', display: 'flex', flexDirection: 'column', gap: 8, animation: 'hvFade 0.12s ease both' }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--hv-ink)' }}>{p.when}</span>
                                    <span style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                        {p.rows.map(([l, v]) => (
                                            <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                                                <span style={{ flex: 1, color: 'var(--hv-body)' }}>{l}</span>
                                                <span style={{ color: 'var(--hv-ink)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
                                            </span>
                                        ))}
                                    </span>
                                    {ch !== null && (
                                        <span style={{ paddingTop: 7, borderTop: '1px solid var(--hv-line)', fontSize: 11.5, color: 'var(--hv-muted)' }}>
                                            <span style={{ color: ch >= 0 ? GOOD : BAD, fontWeight: 500 }}>{`${ch >= 0 ? '+' : '−'}${Math.abs(ch)}%`}</span> {chWord}
                                        </span>
                                    )}
                                </span>
                            )}
                        </span>
                    );
                })}
            </div>
            <span />
            <div style={{ position: 'relative' }}>
                {xIdx.map((idx, j) => (
                    <span key={idx} style={{ position: 'absolute', top: 8, left: `${N <= 1 ? 50 : ((idx / (N - 1)) * 100).toFixed(2)}%`, transform: `translateX(${N <= 1 ? '-50%' : j === 0 ? '0' : j === xIdx.length - 1 ? '-100%' : '-50%'})`, whiteSpace: 'nowrap', fontSize: 11, color: 'var(--hv-muted)' }}>
                        {points[idx]?.short}
                    </span>
                ))}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------- donut + bars breakdown
interface Slice {
    name: string;
    y: number;
}
/** Top 5 + "Other" so the donut stays readable. */
function topSlices(list: Metric[], n = 5): Slice[] {
    const sorted = [...list].filter((r) => r.y > 0).sort((a, b) => b.y - a.y);
    const head = sorted.slice(0, n).map((r) => ({ name: r.x || '(unknown)', y: r.y }));
    const rest = sorted.slice(n).reduce((a, r) => a + r.y, 0);
    return rest > 0 ? [...head, { name: 'Other', y: rest }] : head;
}
const Donut: React.FC<{ slices: Slice[]; hov: number; setHov: (i: number) => void; totalLabel: string; unit: string }> = ({ slices, hov, setHov, totalLabel, unit }) => {
    const [tip, setTip] = useState<{ i: number; x: number; y: number } | null>(null);
    const tot = slices.reduce((a, x) => a + x.y, 0) || 1;
    const R = 64;
    const SW = 18;
    const C = 2 * Math.PI * R;
    const GAP = slices.length > 1 ? 3 : 0;
    let acc = 0;
    return (
        <div style={{ position: 'relative', width: 160, height: 160, flex: 'none' }}>
            <svg width="160" height="160" viewBox="0 0 160 160" style={{ display: 'block', overflow: 'visible' }} aria-hidden="true">
                <circle cx="80" cy="80" r={R} fill="none" stroke="var(--hv-page-2)" strokeWidth={SW} />
                {slices.map((x, i) => {
                    const len = Math.max(0.5, (x.y / tot) * C - GAP);
                    const off = -acc;
                    acc += (x.y / tot) * C;
                    return (
                        <circle
                            key={x.name}
                            cx="80" cy="80" r={R} fill="none" stroke={PIE[i % PIE.length]}
                            strokeWidth={hov === i ? SW + 6 : SW}
                            strokeDasharray={`${len} ${C - len}`}
                            strokeDashoffset={off}
                            transform="rotate(-90 80 80)"
                            style={{ cursor: 'pointer', opacity: hov === -1 || hov === i ? 1 : 0.35, transition: 'opacity .15s ease, stroke-width .15s ease' }}
                            onMouseMove={(e) => {
                                const r = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
                                setHov(i);
                                setTip({ i, x: e.clientX - r.left, y: e.clientY - r.top });
                            }}
                            onMouseLeave={() => { setHov(-1); setTip(null); }}
                        />
                    );
                })}
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', textAlign: 'center' }}>
                <span style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--hv-ink)', fontVariantNumeric: 'tabular-nums' }}>{fmtK(tot)}</span>
                <span style={{ marginTop: 2, maxWidth: 88, fontSize: 11.5, color: 'var(--hv-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{totalLabel}</span>
            </div>
            {tip && slices[tip.i] && (
                <div style={{ position: 'absolute', left: tip.x + 14, top: tip.y + 14, zIndex: 20, pointerEvents: 'none', minWidth: 140, padding: '8px 10px', borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 16px 32px -16px var(--hv-shadow)', fontSize: 12.5, color: 'var(--hv-ink)', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: PIE[tip.i % PIE.length] }} />
                        {slices[tip.i].name}
                    </div>
                    <div style={{ marginTop: 4, display: 'flex', gap: 12, color: 'var(--hv-body)', fontVariantNumeric: 'tabular-nums' }}>
                        <span>{fmtInt(slices[tip.i].y)} {unit}</span>
                        <span style={{ marginLeft: 'auto', color: 'var(--hv-muted)' }}>{Math.round((slices[tip.i].y / tot) * 100)}%</span>
                    </div>
                </div>
            )}
        </div>
    );
};

interface BreakTab {
    id: string;
    label: string;
    rows: Metric[];
    empty?: string;
}
/** Design "Traffic sources / Location / Devices" card: segmented tabs, donut, legend rows. */
const BreakdownCard: React.FC<{ title: string; tabs: BreakTab[]; totalLabel?: string; unit?: string }> = ({ title, tabs, totalLabel = 'Visitors', unit = 'visitors' }) => {
    const [tabId, setTabId] = useState(tabs[0].id);
    const [hov, setHov] = useState(-1);
    const tab = tabs.find((t) => t.id === tabId) ?? tabs[0];
    const slices = topSlices(tab.rows);
    const tot = slices.reduce((a, x) => a + x.y, 0) || 1;
    return (
        <div style={CARD}>
            <CardHead title={title} right={tabs.length > 1 ? <Seg value={tab.id} options={tabs.map((t) => [t.id, t.label] as [string, string])} onChange={(v) => { setTabId(v); setHov(-1); }} /> : undefined} />
            {slices.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px 28px' }}>
                    <Donut slices={slices} hov={hov} setHov={setHov} totalLabel={totalLabel} unit={unit} />
                    <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {slices.map((r, i) => (
                            <div key={r.name} onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(-1)} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, opacity: hov !== -1 && hov !== i ? 0.4 : 1, transition: 'opacity .15s ease', cursor: 'default' }}>
                                <span style={{ flex: 'none', width: 10, height: 10, borderRadius: 3, background: PIE[i % PIE.length] }} />
                                <span style={{ flex: 1, ...ellipsis }} title={r.name}>{r.name}</span>
                                <span style={{ color: 'var(--hv-body)', fontVariantNumeric: 'tabular-nums' }}>{fmtK(r.y)}</span>
                                <span style={{ width: 40, textAlign: 'right', fontSize: 12, color: 'var(--hv-muted)', fontVariantNumeric: 'tabular-nums' }}>{Math.round((r.y / tot) * 100)}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div style={{ padding: '18px 0 6px', fontSize: 13, lineHeight: 1.55, color: 'var(--hv-muted)' }}>{tab.empty ?? 'No data for this period yet.'}</div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------- busiest times heatmap
const HEAT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HEAT_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const HEAT_INDEX = [1, 2, 3, 4, 5, 6, 0]; // Umami rows are Sunday-first
const HeatmapCard: React.FC<{ weekly: number[][]; tz: string }> = ({ weekly, tz }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const [ht, setHt] = useState<{ di: number; h: number; x: number; y: number } | null>(null);
    const rows = HEAT_INDEX.map((i) => Array.from({ length: 24 }, (_, h) => Number(weekly[i]?.[h] ?? 0)));
    const peak = Math.max(0, ...rows.flat());
    const enter = (di: number, h: number) => (e: React.MouseEvent<HTMLSpanElement>) => {
        const card = cardRef.current;
        if (!card) return;
        const r = e.currentTarget.getBoundingClientRect();
        const cr = card.getBoundingClientRect();
        const cx = r.left + r.width / 2 - cr.left;
        setHt({ di, h, x: Math.max(108, Math.min(cr.width - 108, cx)), y: r.top - cr.top });
    };
    const cur = ht ? rows[ht.di][ht.h] : 0;
    return (
        <div ref={cardRef} style={{ ...CARD, position: 'relative' }}>
            <CardHead title="Busiest times" sub={`Visitors by day and hour, in your time zone (${tz}).`} />
            {peak === 0 ? (
                <div style={{ padding: '6px 0', fontSize: 13, color: 'var(--hv-muted)' }}>No visits in this period yet.</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <div style={{ minWidth: 560, display: 'flex', flexDirection: 'column', gap: 3 }} onMouseLeave={() => setHt(null)}>
                        {rows.map((cells, di) => (
                            <div key={HEAT_DAYS[di]} style={{ display: 'grid', gridTemplateColumns: '36px repeat(24,minmax(0,1fr))', gap: 3, alignItems: 'center' }}>
                                <span style={{ fontSize: 11.5, color: 'var(--hv-muted)' }}>{HEAT_DAYS[di]}</span>
                                {cells.map((v, h) => {
                                    const t = peak ? v / peak : 0;
                                    const on = ht?.di === di && ht?.h === h;
                                    return <span key={h} onMouseEnter={enter(di, h)} style={{ height: 20, borderRadius: 4, background: t < 0.04 ? 'var(--hv-page-2)' : `color-mix(in srgb,var(--hv-brand-ink) ${Math.round(12 + t * 88)}%,var(--hv-page-2))`, boxShadow: on ? 'inset 0 0 0 2px var(--hv-ink)' : 'none', cursor: 'default' }} />;
                                })}
                            </div>
                        ))}
                        <div style={{ display: 'grid', gridTemplateColumns: '36px repeat(8,minmax(0,1fr))', gap: 3, marginTop: 4, fontSize: 11, color: 'var(--hv-muted)' }}>
                            <span />
                            {['00', '03', '06', '09', '12', '15', '18', '21'].map((h) => <span key={h}>{h}</span>)}
                        </div>
                    </div>
                </div>
            )}
            {ht && (
                <div role="tooltip" style={{ position: 'absolute', left: ht.x, top: ht.y, transform: 'translate(-50%,calc(-100% - 8px))', zIndex: 60, pointerEvents: 'none', width: 200, padding: '10px 12px', borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 16px 32px -14px var(--hv-shadow)', display: 'flex', flexDirection: 'column', gap: 8, whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--hv-ink)' }}>{HEAT_FULL[ht.di]}</span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {([
                            ['Time', `${String(ht.h).padStart(2, '0')}:00–${String((ht.h + 1) % 24).padStart(2, '0')}:00`],
                            ['Visitors', fmtInt(cur)],
                            ['vs. peak hour', cur >= peak ? 'Busiest' : `${Math.round((cur / peak) * 100)}%`],
                        ] as Array<[string, string]>).map(([l, v]) => (
                            <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                                <span style={{ flex: 1, color: 'var(--hv-body)' }}>{l}</span>
                                <span style={{ color: 'var(--hv-ink)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
                            </span>
                        ))}
                    </span>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------- website tab
const WebsiteBody: React.FC<{ data: WebData; siteName: string | null }> = ({ data, siteName }) => {
    const [metric, setMetric] = useState<'visitors' | 'views'>('visitors');
    const [pgTab, setPgTab] = useState<'viewed' | 'landing'>('viewed');
    const s = data.summary;
    const p = data.previous;
    const rel = (now: number, before: number) => (before ? ((now - before) / before) * 100 : null);
    const bounce = (x: StatsSummary) => (x.visits ? Math.min(1, x.bounces / x.visits) : 0);
    const avg = (x: StatsSummary) => (x.visits ? x.totaltime / x.visits : 0);
    const kpis: Kpi[] = [
        { label: 'Visitors', value: fmtK(s.visitors), pct: rel(s.visitors, p.visitors) },
        { label: 'Page views', value: fmtK(s.pageviews), pct: rel(s.pageviews, p.pageviews) },
        { label: 'Bounce rate', value: s.visits ? `${Math.round(bounce(s) * 100)}%` : '–', pct: s.visits && p.visits ? rel(bounce(s), bounce(p)) : null, upIsGood: false },
        { label: 'Avg. visit time', value: s.visits ? fmtDuration(avg(s)) : '–', pct: s.visits && p.visits ? rel(avg(s), avg(p)) : null },
    ];

    const points = useMemo<ChartPoint[]>(() => {
        const keys = bucketKeys(data.startAt, data.endAt, data.unit, data.timezone);
        const vis = new Map(data.series.sessions.map((m) => [keyOf(m.x, data.unit), Number(m.y) || 0]));
        const pv = new Map(data.series.pageviews.map((m) => [keyOf(m.x, data.unit), Number(m.y) || 0]));
        return keys.map((k) => {
            const v = vis.get(k) ?? 0;
            const w = pv.get(k) ?? 0;
            const l = bucketLabels(k, data.unit);
            return { key: k, short: l.short, when: l.when, value: metric === 'visitors' ? v : w, rows: [['Visitors', fmtInt(v)], ['Page views', fmtInt(w)]] };
        });
    }, [data, metric]);

    const landing = !!data.single && pgTab === 'landing';
    const pageList = landing ? data.single!.entries : data.pages;
    const paged = usePaged(pageList, 5, `${pgTab}|${data.startAt}`);
    const one = data.single;

    return (
        <>
            <KpiRow items={kpis} />
            {s.visitors === 0 && (
                <div style={{ padding: '12px 16px', borderRadius: 8, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', fontSize: 13, lineHeight: 1.55, fontWeight: 500 }}>
                    No visits in this period yet. Share your site link on WhatsApp, Instagram or Google Business; visits show up here within seconds.
                </div>
            )}
            <div style={CARD}>
                <CardHead title="Customer actions" sub={`Clicks on WhatsApp, phone, Maps and email links on ${one ? 'your site' : 'your sites'}.`} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
                    {([
                        ['WhatsApp chats', data.leads.whatsapp],
                        ['Phone calls', data.leads.phone],
                        ['Directions (Maps)', data.leads.maps],
                        ['Emails', data.leads.email],
                    ] as Array<[string, number]>).map(([label, v]) => (
                        <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 14px', borderRadius: 6, background: 'color-mix(in srgb,var(--hv-page-2) 45%,var(--hv-card))' }}>
                            <span style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(v)}</span>
                            <span style={{ fontSize: 12.5, color: 'var(--hv-body)' }}>{label}</span>
                        </div>
                    ))}
                </div>
            </div>
            <div style={{ ...CARD, padding: '18px 18px 14px' }}>
                <CardHead title={metric === 'visitors' ? 'Visitors over time' : 'Page views over time'} right={<Seg value={metric} options={[['visitors', 'Visitors'], ['views', 'Page views']]} onChange={setMetric} />} />
                <LineChart points={points} unit={data.unit} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,380px),1fr))', gap: 16 }}>
                <div style={{ ...CARD, gridColumn: '1 / -1' }}>
                    <CardHead
                        title="Pages"
                        sub={one ? undefined : 'Top pages across all your live sites.'}
                        right={one ? <Seg value={pgTab} options={[['viewed', 'Most viewed'], ['landing', 'Landing pages']]} onChange={setPgTab} /> : undefined}
                    />
                    <div>
                        <Table
                            cols="minmax(0,1fr) 88px"
                            head={[['Page', 'left'], [landing ? 'Entries' : 'Views', 'right']]}
                            empty={landing ? 'No landing pages recorded in this period.' : 'No page views in this period.'}
                            rows={paged.items.map((r) => ({
                                key: r.key,
                                cells: [
                                    <span style={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                        <span style={{ flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.path}>{r.path || '/'}</span>
                                        {r.sub && <span style={{ flex: '0 1000 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12, color: 'var(--hv-muted)' }}>{r.sub}</span>}
                                    </span>,
                                    <span style={{ textAlign: 'right' }}>{fmtK(r.value)}</span>,
                                ],
                            }))}
                        />
                        {pageList.length > 5 && <Pager {...paged} />}
                    </div>
                </div>
                {one && (
                    <>
                        <BreakdownCard
                            title="Traffic sources"
                            tabs={[
                                { id: 'sites', label: 'Websites', rows: one.referrers, empty: 'No referring sites yet. Direct visits (typed link, WhatsApp, apps) are not listed.' },
                                { id: 'campaigns', label: 'Campaigns', rows: one.utmCampaigns, empty: 'Add ?utm_campaign=promo-name to links in ads or your bio to see which campaign brings visitors.' },
                                { id: 'utm', label: 'UTM source', rows: one.utmSources, empty: 'Add ?utm_source=instagram to the links you share to see each channel here.' },
                            ]}
                        />
                        <BreakdownCard title="Location" tabs={[{ id: 'countries', label: 'Countries', rows: one.countries }, { id: 'cities', label: 'Cities', rows: one.cities }]} />
                        <BreakdownCard title="Devices" tabs={[{ id: 'type', label: 'Device type', rows: one.devices }, { id: 'browser', label: 'Browser', rows: one.browsers }]} />
                    </>
                )}
            </div>
            {one?.weekly && <HeatmapCard weekly={one.weekly} tz={data.timezone} />}
            {!one && (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--hv-muted)' }}>
                    Pick a single website{siteName ? '' : ' above'} to see traffic sources, locations, devices and busiest times.
                </p>
            )}
        </>
    );
};

const WebsiteSkeleton: React.FC = () => (
    <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
            {[0, 1, 2, 3].map((i) => <Skel key={i} h={108} />)}
        </div>
        <Skel h={134} />
        <Skel h={306} />
        <Skel h={290} />
    </>
);

// ---------------------------------------------------------------- video tab
/** A post HyVay published, flattened for the tables. */
interface APost {
    key: string;
    platform: SocialPlatform;
    account: SocialAccount;
    videoId: string;
    videoName: string;
    title: string;
    url: string | null;
    thumb: string | null;
    publishedAt: string;
    metrics: PostMetrics | null;
    metricsAt: string | null;
    metricsError: string | null;
}
const toAPost = (p: ScheduledPost): APost => ({
    key: p.id, platform: p.platform, account: p.account, videoId: p.videoId, videoName: p.videoName || 'Untitled video',
    title: p.videoName || p.caption || 'Untitled video', url: p.externalUrl, thumb: null, publishedAt: p.publishedAt || p.scheduledAt,
    metrics: p.metrics, metricsAt: p.metricsAt, metricsError: p.metricsError,
});

interface VidAgg {
    views: number;
    likes: number;
    comments: number;
    shares: number;
    saves: number;
    posts: number;
}
const emptyAgg = (): VidAgg => ({ views: 0, likes: 0, comments: 0, shares: 0, saves: 0, posts: 0 });
function addPost(a: VidAgg, p: APost) {
    const m = p.metrics ?? {};
    a.views += m.views ?? 0;
    a.likes += m.likes ?? 0;
    a.comments += m.comments ?? 0;
    a.shares += m.shares ?? 0;
    a.saves += m.saves ?? 0;
    a.posts += 1;
}
const engagement = (a: VidAgg) => (a.views ? ((a.likes + a.comments + a.shares + a.saves) / a.views) * 100 : null);
const fmtPct = (v: number | null) => (v === null ? '–' : `${v.toFixed(v >= 10 ? 0 : 1)}%`);
const fmtDay = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const accName = (a: SocialAccount) => `${a.name}${a.status === 'DISCONNECTED' ? ' (disconnected)' : ''}`;

const VideoBody: React.FC<{ posts: APost[]; range: VideoRange; videoId: string }> = ({ posts, range, videoId }) => {
    const navigate = useNavigate();
    const list = useMemo(() => {
        const since = range === 'all' ? 0 : Date.now() - RANGE_MS[range];
        return posts.filter((p) => (videoId === 'all' || p.videoId === videoId) && (!since || new Date(p.publishedAt).getTime() >= since));
    }, [posts, range, videoId]);

    const total = useMemo(() => list.reduce((a, p) => (addPost(a, p), a), emptyAgg()), [list]);
    const platforms = useMemo(() => {
        const m = new Map<SocialPlatform, VidAgg>();
        for (const p of list) addPost(m.get(p.platform) ?? m.set(p.platform, emptyAgg()).get(p.platform)!, p);
        return [...m.entries()].map(([k, a]) => ({ k, a }));
    }, [list]);
    const accounts = useMemo(() => {
        const m = new Map<string, { name: string; platform: SocialPlatform; a: VidAgg }>();
        for (const p of list) {
            const id = p.account?.id ?? `${p.platform}`;
            if (!m.has(id)) m.set(id, { name: p.account ? accName(p.account) : PLATFORM_LABEL[p.platform], platform: p.platform, a: emptyAgg() });
            addPost(m.get(id)!.a, p);
        }
        return [...m.entries()].map(([id, x]) => ({ id, ...x })).sort((x, y) => y.a.views - x.a.views);
    }, [list]);
    const videos = useMemo(() => {
        const m = new Map<string, { name: string; a: VidAgg; plats: Set<SocialPlatform> }>();
        for (const p of list) {
            if (!m.has(p.videoId)) m.set(p.videoId, { name: p.videoName || 'Untitled video', a: emptyAgg(), plats: new Set() });
            const v = m.get(p.videoId)!;
            addPost(v.a, p);
            v.plats.add(p.platform);
        }
        return [...m.entries()].map(([id, x]) => ({ id, ...x })).sort((x, y) => y.a.views - x.a.views);
    }, [list]);
    const recent = useMemo(() => [...list].sort((x, y) => y.publishedAt.localeCompare(x.publishedAt)), [list]);
    const paged = usePaged(videos, 5, `${range}|${videoId}`);
    const pagedPosts = usePaged(recent, 8, `${range}|${videoId}`);

    const lastRead = list.reduce<string | null>((acc, p) => (p.metricsAt && (!acc || p.metricsAt > acc) ? p.metricsAt : acc), null);
    const failed = list.filter((p) => p.metricsError).length;
    const pending = list.filter((p) => !p.metrics && !p.metricsError).length;

    if (!list.length) {
        return (
            <EmptyBox
                title={posts.length ? 'No posts in this period' : 'No published videos yet'}
                text={posts.length ? 'None of your videos went out in the selected period. Pick a longer range to see earlier posts.' : 'Post a video to YouTube, TikTok, Instagram and more from the Scheduler. Views, likes and shares show up here once the platforms report them.'}
                action={posts.length ? undefined : <FillButton onClick={() => navigate('/dashboard/scheduler')}>Open Scheduler</FillButton>}
            />
        );
    }

    return (
        <>
            <KpiRow
                items={[
                    { label: 'Views', value: fmtK(total.views), note: `across ${total.posts} post${total.posts === 1 ? '' : 's'}` },
                    { label: 'Likes', value: fmtK(total.likes), note: `${fmtK(total.comments)} comments · ${fmtK(total.shares)} shares` },
                    { label: 'Engagement rate', value: fmtPct(engagement(total)), note: 'likes, comments, shares and saves per view' },
                    { label: 'Posts published', value: String(total.posts), note: `on ${platforms.length} platform${platforms.length === 1 ? '' : 's'}` },
                ]}
            />
            <p style={{ margin: '-4px 0 0', fontSize: 12.5, color: 'var(--hv-muted)' }}>
                Lifetime numbers per post, as reported by each platform{lastRead ? `; last read ${fmtAgo(lastRead).toLowerCase()}` : ''}.
                {pending > 0 && ` ${pending} post${pending === 1 ? ' has' : 's have'} no numbers yet; platforms can take a few hours.`}
                {failed > 0 && <span style={{ color: BAD }}>{` ${failed} post${failed === 1 ? '' : 's'} could not be read (the account may need reconnecting).`}</span>}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,380px),1fr))', gap: 16 }}>
                <BreakdownCard
                    title="By platform"
                    totalLabel="Views"
                    unit="views"
                    tabs={[
                        { id: 'views', label: 'Views', rows: platforms.map(({ k, a }) => ({ x: PLATFORM_LABEL[k], y: a.views })), empty: 'No views reported yet.' },
                        { id: 'likes', label: 'Likes', rows: platforms.map(({ k, a }) => ({ x: PLATFORM_LABEL[k], y: a.likes })), empty: 'No likes reported yet.' },
                    ]}
                />
                <div style={CARD}>
                    <CardHead title="By account" />
                    <Table
                        cols="minmax(140px,1.6fr) minmax(90px,1fr) minmax(50px,0.6fr) minmax(60px,0.8fr) minmax(60px,0.8fr) minmax(80px,0.9fr)"
                        head={[['Account', 'left'], ['Channel', 'left'], ['Posts', 'right'], ['Views', 'right'], ['Likes', 'right'], ['Engagement', 'right']]}
                        rows={accounts.map((r) => ({
                            key: r.id,
                            cells: [
                                <span style={ellipsis} title={r.name}>{r.name}</span>,
                                <span style={{ ...ellipsis, color: 'var(--hv-body)' }}>{PLATFORM_LABEL[r.platform]}</span>,
                                <span style={{ textAlign: 'right' }}>{r.a.posts}</span>,
                                <span style={{ textAlign: 'right' }}>{fmtK(r.a.views)}</span>,
                                <span style={{ textAlign: 'right' }}>{fmtK(r.a.likes)}</span>,
                                <span style={{ textAlign: 'right' }}>{fmtPct(engagement(r.a))}</span>,
                            ],
                        }))}
                    />
                </div>
            </div>
            <div style={CARD}>
                <CardHead title="Posts" sub="Every post HyVay published, newest first." />
                <div>
                    <Table
                        cols="minmax(0,1fr) 72px 72px 72px"
                        head={[['Post', 'left'], ['Views', 'right'], ['Likes', 'right'], ['Comments', 'right']]}
                        rows={pagedPosts.items.map((p) => ({
                            key: p.key,
                            cells: [
                                <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                                    <span aria-hidden="true" style={{ flex: 'none', width: 40, height: 40, borderRadius: 6, overflow: 'hidden', background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--hv-muted)', fontSize: 10, fontWeight: 700 }}>
                                        {p.thumb ? <img src={p.thumb} alt="" loading="lazy" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} /> : PLATFORM_LABEL[p.platform].slice(0, 2).toUpperCase()}
                                    </span>
                                    <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {p.url
                                                ? <a href={p.url} target="_blank" rel="noopener noreferrer" title={p.title} style={{ ...ellipsis, color: 'var(--hv-ink)', textDecoration: 'none', fontWeight: 500 }}>{p.title}</a>
                                                : <span title={p.title} style={{ ...ellipsis, fontWeight: 500 }}>{p.title}</span>}
                                        </span>
                                        <span style={{ ...ellipsis, fontSize: 12, color: 'var(--hv-muted)' }}>{`${accName(p.account)} · ${PLATFORM_LABEL[p.platform]} · ${fmtDay(p.publishedAt)}`}</span>
                                    </span>
                                </span>,
                                <span style={{ textAlign: 'right' }}>{p.metrics?.views !== undefined ? fmtK(p.metrics.views) : '–'}</span>,
                                <span style={{ textAlign: 'right' }}>{p.metrics?.likes !== undefined ? fmtK(p.metrics.likes) : '–'}</span>,
                                <span style={{ textAlign: 'right' }}>{p.metrics?.comments !== undefined ? fmtK(p.metrics.comments) : '–'}</span>,
                            ],
                        }))}
                    />
                    {recent.length > 8 && <Pager {...pagedPosts} />}
                </div>
            </div>
            {videos.length > 0 && (
                <div style={CARD}>
                    <CardHead title="Top videos" />
                    <div>
                        <Table
                            cols="minmax(0,1fr) 72px 72px 72px 72px"
                            head={[['Video', 'left'], ['Posts', 'right'], ['Views', 'right'], ['Likes', 'right'], ['Shares', 'right']]}
                            rows={paged.items.map((v) => ({
                                key: v.id,
                                cells: [
                                    <span style={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                        <span style={{ flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} title={v.name}>{v.name}</span>
                                        <span style={{ flex: '0 1000 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12, color: 'var(--hv-muted)' }}>{[...v.plats].map((x) => PLATFORM_LABEL[x]).join(', ')}</span>
                                    </span>,
                                    <span style={{ textAlign: 'right' }}>{v.a.posts}</span>,
                                    <span style={{ textAlign: 'right' }}>{fmtK(v.a.views)}</span>,
                                    <span style={{ textAlign: 'right' }}>{fmtK(v.a.likes)}</span>,
                                    <span style={{ textAlign: 'right' }}>{fmtK(v.a.shares)}</span>,
                                ],
                            }))}
                        />
                        {videos.length > 5 && <Pager {...paged} />}
                    </div>
                </div>
            )}
        </>
    );
};

// ---------------------------------------------------------------- page
const TABS: Array<{ key: 'website' | 'video'; label: string; icon: string; isNew?: boolean }> = [
    { key: 'website', label: 'Website', icon: 'M3.5 12a8.5 8.5 0 1 0 17 0 8.5 8.5 0 0 0-17 0z M3.5 12h17 M12 3.5c2.4 2.4 3.5 5.3 3.5 8.5s-1.1 6.1-3.5 8.5c-2.4-2.4-3.5-5.3-3.5-8.5s1.1-6.1 3.5-8.5z' },
    { key: 'video', label: 'Video', icon: 'M5.5 5h9A2.5 2.5 0 0 1 17 7.5v9a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 3 16.5v-9A2.5 2.5 0 0 1 5.5 5z M17 10l3.4-2.1a.4.4 0 0 1 .6.3v7.6a.4.4 0 0 1-.6.3L17 14', isNew: true },
];

export const DashAnalytics: React.FC = () => {
    const dash = useDash();
    const navigate = useNavigate();
    const [tab, setTab] = useState<'website' | 'video'>('website');

    // ---- website state
    const liveSites = useMemo<DashProject[]>(() => dash.projects.filter((p) => p.kind === 'site' && p.status === 'live'), [dash.projects]);
    const [siteId, setSiteId] = useState('all');
    const [range, setRange] = useState<AnalyticsRange>('7d');
    const [web, setWeb] = useState<WebState>({ kind: 'loading' });
    const [webTick, setWebTick] = useState(0);
    const reqRef = useRef(0);
    const site = siteId === 'all' ? null : liveSites.find((p) => p.id === siteId) ?? null;

    useEffect(() => {
        if (siteId !== 'all' && !dash.projectsLoading && !liveSites.some((p) => p.id === siteId)) setSiteId('all');
    }, [siteId, liveSites, dash.projectsLoading]);

    useEffect(() => {
        if (tab !== 'website') return;
        if (dash.projectsLoading && !dash.projects.length) { setWeb({ kind: 'loading' }); return; }
        const req = ++reqRef.current;
        setWeb({ kind: 'loading' });
        (async () => {
            try {
                let next: WebState;
                if (site) {
                    const d = await analyticsService.dashboard(site.id, range, TZ);
                    next = !d.enabled ? { kind: 'disabled' } : !d.published ? { kind: 'nolive' } : { kind: 'ready', data: fromSite(d, range, hostOf(site.publishedUrl)) };
                } else {
                    const o = await analyticsOverviewService.overview(range, TZ);
                    next = !o.enabled ? { kind: 'disabled' } : o.empty ? { kind: 'nolive' } : { kind: 'ready', data: fromOverview(o, range) };
                }
                if (req === reqRef.current) setWeb(next);
            } catch (e: any) {
                if (req === reqRef.current) setWeb({ kind: 'error', message: e?.response?.data?.message || e?.message || 'Could not load analytics.' });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, site?.id, range, webTick, dash.projectsLoading]);

    // Single site: keep "online now" fresh like the studio panel does.
    const [online, setOnline] = useState<number | null>(null);
    useEffect(() => {
        setOnline(web.kind === 'ready' ? web.data.active : null);
    }, [web]);
    useEffect(() => {
        if (tab !== 'website' || !site || web.kind !== 'ready') return;
        const t = window.setInterval(() => {
            analyticsService.active(site.id).then(setOnline).catch(() => undefined);
        }, 30_000);
        return () => window.clearInterval(t);
    }, [tab, site, web.kind]);

    // ---- video state
    const [posts, setPosts] = useState<ScheduledPost[] | null>(null);
    const [postsErr, setPostsErr] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const [vRange, setVRange] = useState<VideoRange>('all');
    const [videoId, setVideoId] = useState('all');
    const loadedVideo = useRef(false);

    const loadPosts = useCallback(async (refresh: boolean, manual: boolean) => {
        setPostsErr(null);
        if (refresh) setRefreshing(true);
        try {
            const reload = async () => {
                const all = await socialService.posts();
                setPosts(all.filter((p) => p.status === 'PUBLISHED'));
            };
            if (!refresh) {
                await reload();
                return;
            }
            const r = await socialService.refreshInsights().catch((e) => {
                if (manual) dash.notify('error', 'Could not refresh numbers from the platforms. Showing the last ones read.');
                void e;
                return null;
            });
            if (!r || r.refreshed > 0 || manual) await reload();
            if (manual && r) dash.notify('success', r.refreshed ? `Updated ${r.refreshed} post${r.refreshed === 1 ? '' : 's'}.` : 'Numbers are already up to date.');
        } catch (e: any) {
            setPostsErr(e?.response?.data?.message || e?.message || 'Could not load video insights.');
        } finally {
            if (refresh) setRefreshing(false);
        }
    }, [dash]);

    useEffect(() => {
        if (tab !== 'video' || loadedVideo.current) return;
        loadedVideo.current = true;
        // Show stored numbers first, then re-read the stale ones from the platforms.
        void loadPosts(false, false).then(() => loadPosts(true, false));
    }, [tab, loadPosts]);

    const videoOpts = useMemo<Opt[]>(() => {
        const m = new Map<string, string>();
        for (const p of posts ?? []) if (!m.has(p.videoId)) m.set(p.videoId, p.videoName || 'Untitled video');
        return [{ id: 'all', label: 'All videos' }, ...[...m.entries()].map(([id, label]) => ({ id, label }))];
    }, [posts]);

    const allPosts = useMemo<APost[]>(() => (posts ?? []).map(toAPost), [posts]);

    // ---- render
    const siteOpts: Opt[] = [{ id: 'all', label: 'All websites' }, ...liveSites.map((p) => ({ id: p.id, label: p.name, sub: hostOf(p.publishedUrl) }))];
    const isWeb = tab === 'website';
    const pickLabel = isWeb ? (site ? site.name : 'All websites') : (videoOpts.find((o) => o.id === videoId)?.label ?? 'All videos');

    let body: React.ReactNode;
    if (isWeb) {
        if (web.kind === 'loading') body = <WebsiteSkeleton />;
        else if (web.kind === 'error') body = <EmptyBox title="Analytics could not load" text={web.message} action={<OutlineButton onClick={() => setWebTick((n) => n + 1)}>Try again</OutlineButton>} />;
        else if (web.kind === 'disabled') body = <EmptyBox title="Visitor analytics isn't switched on yet" text="Visitor counting is not configured on this server. Your sites keep working; numbers will show up here as soon as it is on." />;
        else if (web.kind === 'nolive') body = (
            <EmptyBox
                title={site ? 'This site is not live' : 'No live website yet'}
                text={site ? 'Publish it again to start counting visitors.' : 'Publish a site to start counting visitors, page views and WhatsApp clicks. Numbers show up here within seconds of the first visit.'}
                action={<FillButton onClick={() => navigate('/dashboard/projects')}>Publish a site</FillButton>}
            />
        );
        else body = <WebsiteBody key={`${site?.id ?? 'all'}|${range}`} data={web.data} siteName={site?.name ?? null} />;
    } else {
        if (postsErr && !posts) body = <EmptyBox title="Video insights could not load" text={postsErr} action={<OutlineButton onClick={() => void loadPosts(false, false)}>Try again</OutlineButton>} />;
        else if (!posts) body = <WebsiteSkeleton />;
        else body = <VideoBody posts={allPosts} range={vRange} videoId={videoId} />;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28, animation: 'hvRise 0.4s cubic-bezier(0.22,0.9,0.24,1) both' }}>
            <style>{PAGE_CSS}</style>
            <PageHead title="Analytics" lead="See how your sites and videos perform across every channel." />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: -12 }}>
                <nav aria-label="Analytics" style={{ display: 'flex', gap: 24, borderBottom: '1px solid var(--hv-line)' }}>
                    {TABS.map((t) => {
                        const on = tab === t.key;
                        return (
                            <button key={t.key} type="button" aria-current={on ? 'page' : undefined} onClick={() => setTab(t.key)} className="hv-an-tab" style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40, marginBottom: -1, padding: '0 2px', border: 0, borderBottom: `2px solid ${on ? 'var(--hv-ink)' : 'transparent'}`, background: 'transparent', color: on ? 'var(--hv-ink)' : 'var(--hv-body)', fontSize: 13.5, fontWeight: on ? 600 : 400, cursor: 'pointer' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }} aria-hidden="true"><path d={t.icon} /></svg>
                                <span>{t.label}</span>
                                {t.isNew && <span style={{ flex: 'none', display: 'flex', alignItems: 'center', height: 15, padding: '0 5px', borderRadius: 999, background: '#D93F33', color: '#FFFDF7', fontFamily: FONT_SANS, fontSize: 8, fontWeight: 700, letterSpacing: '0.05em' }}>NEW</span>}
                            </button>
                        );
                    })}
                </nav>
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <SelectBox
                            key={tab}
                            value={isWeb ? siteId : videoId}
                            options={isWeb ? siteOpts : videoOpts}
                            onChange={isWeb ? setSiteId : setVideoId}
                            ariaLabel={isWeb ? 'Website' : 'Video'}
                            searchPh={isWeb ? 'Search websites' : 'Search videos'}
                            display={<span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500, textAlign: 'left' }}>{pickLabel}</span>}
                        />
                        {isWeb ? (
                            <RangeSelect value={range} options={RANGES} onChange={(v) => setRange(v as AnalyticsRange)} />
                        ) : (
                            <RangeSelect value={vRange} options={[['all', 'All time'], ...RANGES]} onChange={(v) => setVRange(v as VideoRange)} />
                        )}
                        <span style={{ flex: 1 }} />
                        {isWeb && web.kind === 'ready' && online !== null && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 24, padding: '0 10px', borderRadius: 999, background: 'var(--hv-page-2)', fontSize: 12, color: 'var(--hv-body)' }}>
                                <span style={{ width: 6, height: 6, borderRadius: 999, background: online > 0 ? '#3FB56B' : 'var(--hv-line-2)', boxShadow: online > 0 ? '0 0 0 3px color-mix(in srgb,#3FB56B 22%,transparent)' : 'none' }} />
                                {online} online now
                            </span>
                        )}
                        {!isWeb && posts && (
                            <OutlineButton onClick={() => void loadPosts(true, true)} disabled={refreshing} aria-label="Refresh numbers from the platforms">
                                <RefreshIcon spin={refreshing} />
                                {refreshing ? 'Refreshing…' : 'Refresh'}
                            </OutlineButton>
                        )}
                    </div>
                    {body}
                </div>
            </div>
        </div>
    );
};

export default DashAnalytics;
